import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiCheckCircle, FiTarget, FiZap, FiBarChart } from 'react-icons/fi';
import { caseStudies } from '../data/caseStudies';
import SEO from '../components/common/SEO';
import SafeIcon from '../components/common/SafeIcon';
import AuditForm from '../components/sections/AuditForm';

const CaseStudyDetailPage = () => {
  const { studyId } = useParams();
  const study = caseStudies.find(s => s.id === studyId);

  if (!study) return <Navigate to="/case-studies" replace />;

  return (
    <>
      <SEO title={`${study.client} Strategy | Alberta SEO Pro`} description={study.title} />
      
      <div className="pt-20 bg-slate-900 min-h-[400px] flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-orange-400 font-bold text-sm mb-8 hover:text-orange-300 transition-colors">
            <SafeIcon icon={FiArrowLeft} /> Back to Results
          </Link>
          <div className="max-w-3xl">
            <span className="px-4 py-1 bg-orange-600/20 border border-orange-600/30 text-orange-400 rounded-full text-xs font-bold uppercase tracking-widest mb-6 inline-block">
              {study.industry} Case Study
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {study.title}
            </h1>
            <p className="text-xl text-slate-300">
              Client: <span className="text-white font-bold">{study.client}</span> • Location: {study.location}
            </p>
          </div>
        </div>
      </div>

      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-16">
            <div className="lg:col-span-2 space-y-16">
              {/* Problem */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 text-red-600 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiTarget} />
                  </div>
                  The Challenge
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {study.problem}
                </p>
              </div>

              {/* Strategy */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiZap} />
                  </div>
                  The Strategy
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  {study.solution}
                </p>
                <div className="grid md:grid-cols-1 gap-4">
                  {study.strategy.map((item, i) => (
                    <div key={i} className="flex gap-4 p-6 bg-slate-50 rounded-2xl border border-slate-100">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1 shrink-0" />
                      <span className="text-slate-700 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Result */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 text-green-600 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiBarChart} />
                  </div>
                  The Results
                </h2>
                <div className="bg-slate-900 rounded-[2rem] p-10 text-white">
                  <p className="text-2xl font-bold mb-10 text-orange-400">"{study.result}"</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                    {study.stats.map((stat, i) => (
                      <div key={i}>
                        <span className="block text-4xl font-bold mb-1">{stat.value}</span>
                        <span className="text-slate-400 uppercase text-xs font-black tracking-widest">{stat.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar CTA */}
            <div className="lg:col-span-1">
              <div className="sticky top-32">
                <AuditForm 
                  title="Get the same results" 
                  subtitle={`We'll show you exactly how we'd apply this strategy to your business.`}
                  buttonText="Let's Talk Strategy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CaseStudyDetailPage;